/* eslint-disable react/jsx-props-no-spreading */

/* eslint-disable react/no-danger */
import { loadTwitter, loadInstagram, loadTikTok, loadFacebook } from '@folklore/services';
import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import useFacebook from '../../hooks/useFacebook';
import useIsVisible from '../../hooks/useIsVisible';
// import { useWindowSize } from '../../contexts/SiteContext';
import * as AppPropTypes from '../../lib/PropTypes';

import Video from './Video';

import styles from '../../../styles/partials/embed.module.scss';

const propTypes = {
    embed: AppPropTypes.embed,
    size: PropTypes.shape({ width: PropTypes.string, height: PropTypes.string }),
    withoutControls: PropTypes.bool,
    withControls: PropTypes.bool,
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool,
    initialMuted: PropTypes.bool,
    fullWidth: PropTypes.bool,
    apiRef: AppPropTypes.ref,
    style: PropTypes.shape({
        maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    className: PropTypes.string,
    iframeClassName: PropTypes.string,
};

const defaultProps = {
    embed: null,
    size: null,
    withoutControls: false,
    withControls: false,
    autoPlay: false,
    loop: false,
    initialMuted: false,
    fullWidth: false,
    apiRef: null,
    style: null,
    className: null,
    iframeClassName: null,
};

const Embed = ({
    embed,
    size,
    withoutControls,
    withControls,
    autoPlay,
    loop,
    initialMuted,
    fullWidth,
    apiRef,
    style,
    className,
    iframeClassName,
    ...props
}) => {
    const {
        provider = null,
        metadata = null,
        iframeUrl = null,
        type,
        data = null,
        video = null,
    } = embed || {};
    const { width: customWidth = null, height: customHeight = null } = size || {};

    const {
        width: embedwidth = customWidth,
        height: embedheight = customHeight,
        html = null,
    } = metadata || {};

    const isVideo = type === 'video';
    const isFixed =
        provider === '@bandcamp' ||
        provider === 'instagram' ||
        provider === 'facebook' ||
        provider === 'tiktok' ||
        type === 'newsletter';

    const { ref, visible: isVisible } = useIsVisible({
        rootMargin: '200px',
        persist: true,
    });

    const { FB } = useFacebook();

    useEffect(() => {
        const { current: element } = ref;
        if (provider === 'twitter' && isVisible) {
            loadTwitter().then((twttr) => {
                twttr.widgets.load(element);
            });
        } else if (provider === 'instagram' && isVisible) {
            loadInstagram().then((instgrm) => {
                instgrm.Embeds.process();
            });
        } else if (provider === 'tiktok' && isVisible) {
            loadTikTok();
        } else if (provider === 'facebook' && isVisible) {
            loadFacebook({ version: 'v16.0' });
        }
    }, [provider, isVisible]);

    useEffect(() => {
        const { current: element } = ref;
        if (provider === 'facebook' && isVisible && FB !== null) {
            setTimeout(() => {
                FB.XFBML.parse(element.parentNode);
            }, 500);
        }
    }, [iframeUrl, provider, isVisible, FB]);

    // prettier-ignore
    const embedratio = isNumber(embedwidth) && isNumber(embedheight) && !isFixed
        ? embedwidth / embedheight
        : null;

    const hasSize =
        (embedratio !== null || (embedwidth === '100%' && isNumber(embedheight))) && !isFixed;
    const hasMaxWidth =
        !fullWidth && !isVideo && !isFixed && isNumber(embedwidth) && embedwidth > 0;

    const styleBlock = {
        paddingBottom: embedratio !== null ? `${100 * embedratio}%` : null,
        minWidth: hasMaxWidth ? embedwidth || null : null,
        height: embedratio === null && embedwidth === '100%' ? embedheight : 'auto',
    };

    const playerAusha = iframeUrl !== null && iframeUrl.includes('player.ausha.co');

    // Facebook + tiktok
    const divProps =
        provider === 'facebook' && data !== null
            ? Object.keys(data).reduce(
                  (map, key) => ({
                      ...map,
                      [`data-${key}`]: data[key],
                  }),
                  {
                      'data-href': 'cite',
                      'data-adapt-container-width': false,
                  },
              )
            : null;

    const innerProps =
        data !== null && provider !== 'facebook'
            ? Object.keys(data).reduce(
                  (map, key) => ({
                      ...map,
                      [`data-${provider === 'instagram' ? 'instgrm-' : ''}${key}`]: data[key],
                  }),
                  provider === 'instagram'
                      ? {
                            'data-instgrm-captioned': true,
                        }
                      : null,
              )
            : null;

    return !isVideo ? (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fullWidth]: true || fullWidth,
                    [styles.isVideo]: isVideo,
                    [styles.isFixed]: isFixed,
                    [styles[provider]]: provider !== null,
                    [className]: className !== null,
                },
            ])}
            style={style}
        >
            {embed !== null ? (
                <div className={styles.embed} style={styleBlock} ref={ref}>
                    {iframeUrl !== null && !isFixed ? (
                        <iframe
                            src={iframeUrl}
                            className={classNames([
                                styles.iframe,
                                {
                                    [styles.playerAusha]: playerAusha,
                                    [styles.fullscreen]: hasSize,
                                    [iframeClassName]: iframeClassName !== null,
                                },
                            ])}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title="Embed"
                            width={embedwidth}
                            height={embedheight}
                        />
                    ) : (
                        <div
                            {...innerProps}
                            {...divProps}
                            {...(provider === 'facebook'
                                ? { 'data-href': iframeUrl, 'data-show-text': true }
                                : null)}
                            className={classNames([
                                styles.divContainer,
                                {
                                    [iframeClassName]: iframeClassName !== null,
                                    [styles.fullscreen]: hasSize,
                                    'fb-post': provider === 'facebook',
                                },
                            ])}
                            // style={{ width: window.innerWidth - 40, height: 600 }}
                            dangerouslySetInnerHTML={{ __html: html }}
                        />
                    )}
                </div>
            ) : null}
        </div>
    ) : (
        <div
            className={classNames([
                styles.videoWrapper,
                { [styles.vertical]: embedheight / embedwidth > 1 },
            ])}
            style={style}
        >
            <Video
                {...props}
                className={classNames([styles.videoContainer, { [className]: className !== null }])}
                videoClassName={styles.video}
                iframeClassName={styles.iframe}
                video={video || embed}
                withoutControls={withoutControls && !withControls}
                forceControls={withControls}
                autoPlay={autoPlay}
                loop={loop}
                initialMuted={initialMuted}
                apiRef={apiRef}
                // style={{ paddingBottom: `${(embedheight / embedwidth) * 100}%` }}
            />
        </div>
    );
};

Embed.propTypes = propTypes;
Embed.defaultProps = defaultProps;

export default Embed;
